

//Utility logic for listing vertex data from LmvBufferGeometry interleaved buffers

import { LmvVector3 } from './LmvVector3';
import { LmvMatrix4 } from './LmvMatrix4';

//These functions work for both workers side interleaved buffer structures
//and main thread side LmvBufferGeometry instances. The difference in naming
//if the index attribute on both sides is super annoying and should be cleaned up.


/** Works for BufferGeometry as well as THREE.BufferGeometry. Supports interleaved and non-interleaved buffers.
 *   @param {BufferGeometry|THREE.BufferGeometry} geom
 *   @returns {number}
 */
export function getVertexCount(geom) {

  if (!geom) {
    return 0;
  }

  if (geom.vb) {
    // interleaved
    return geom.vb.length / geom.vbstride;
  }
  // no interleaved buffer. Return count from position attribute or 0
  return geom.attributes.position ? geom.attributes.position.array.length / (geom.attributes.position.itemSize || 3) : 0;
}

export function getIndicesCount(geometry) {

  const indices = getIndexBuffer(geometry);

  if (indices) {

    var groups = geometry.groups;

    if (!groups || groups.length === 0) {
      return indices.length;
    }

    let res = 0;

    for (var oi = 0, ol = groups.length; oi < ol; ++oi) {
      res += groups[oi].count;
    }

    return res;

  } else {
    return getVertexCount(geometry);
  }
}

var _p, _n, _uv;
var _normalsMatrix;

/**
 * Returns a function(vertexIndex, outVec3) that reads a decoded position into outVec3.
 * @param {BufferGeometry|MeshData} mdata
 * @returns {((idx: number, out: {x,y,z}) => void) | null}
 */
export function makeReadPositionFunc(mdata) {
  const attributes = mdata.attributes;
  // Resolve the position attribute descriptor. Be careful, 2D buffers
  // don't use the 'position' attribute for positions. Reject those.
  let pattr = mdata.vblayout ? mdata.vblayout.position : attributes.position || null;

  if (!pattr) return null;

  const positions = mdata.vb || pattr.array;
  const poffset = pattr.offset || 0;
  const stride = mdata.vb ? mdata.vbstride : 3;
  return function (idx, out) {
    const p = idx * stride + poffset;
    out.x = positions[p];
    out.y = positions[p + 1];
    out.z = positions[p + 2];
  };
}

/**
 * Returns a function(vertexIndex, outVec3) that reads a decoded normal into outVec3.
 * @param {BufferGeometry|MeshData} geometry
 * @returns {((idx: number, out: {x,y,z}) => void) | null}
 */
function makeReadNormalFunc(geometry) {
  const attributes = geometry.attributes;
  let nattr = geometry.vblayout ? geometry.vblayout.normal : attributes.normal || null;

  if (!nattr) return null;
  if (!nattr.array && (nattr.itemSize !== 3 || nattr.bytesPerItem !== 4)) return null;

  const normals = geometry.vb || nattr.array;
  const noffset = nattr.offset || 0;
  const stride = geometry.vb ? geometry.vbstride : 3;
  return function (idx, out) {
    const p = idx * stride + noffset;
    out.x = normals[p];
    out.y = normals[p + 1];
    out.z = normals[p + 2];
  };
}

/**
 * Extracts the indices array
 * @param {BufferGeometry|MeshData} geometry
 * @returns {Uint16Array|null|undefined}
 */
function getIndexBuffer(geometry) {
  return geometry.ib || geometry.indices || (geometry.index ? geometry.index.array : null);
}

export function enumMeshVertices(geometry, callback, matrix) {

  if (!_p) {
    _p = new LmvVector3();
    _n = new LmvVector3();
    _uv = new LmvVector3();
  }

  if (matrix) {
    if (!_normalsMatrix)
    _normalsMatrix = new THREE.Matrix3();

    _normalsMatrix.getNormalMatrix(matrix);
  }

  const readPosition = makeReadPositionFunc(geometry);
  const readNormal = makeReadNormalFunc(geometry);
  if (!readPosition) return;

  var vcount = getVertexCount(geometry);

  for (var i = 0; i < vcount; i++) {

    readPosition(i, _p);

    if (matrix)
    _p.applyMatrix4(matrix);

    if (readNormal) {
      readNormal(i, _n);

      if (matrix) {
        _n.applyMatrix3(_normalsMatrix);
      }
    }

    callback(_p, readNormal ? _n : null, null /*, _uv*/, i);
  }
}

export function enumMeshIndices(geometry, callback) {
  const indices = getIndexBuffer(geometry);

  if (indices) {

    let groups = geometry.groups;

    if (!groups || groups.length === 0) {
      groups = [{ start: 0, count: indices.length, index: 0 }];
    }

    for (let oi = 0, ol = groups.length; oi < ol; ++oi) {

      let start = groups[oi].start;
      let count = groups[oi].count;
      let index = groups[oi].index;

      for (let i = start, il = start + count; i < il; i += 3) {

        let a = index + indices[i];
        let b = index + indices[i + 1];
        let c = index + indices[i + 2];

        callback(a, b, c);
      }
    }
  } else {

    let vcount = getVertexCount(geometry);

    for (let i = 0; i < vcount; i++) {

      let a = 3 * i;
      let b = 3 * i + 1;
      let c = 3 * i + 2;

      callback(a, b, c);
    }
  }
}


var vA, vB, vC, nA, nB, nC;

export function enumMeshTriangles(geometry, callback) {

  var a, b, c;

  if (!vA) {
    vA = new LmvVector3();
    vB = new LmvVector3();
    vC = new LmvVector3();

    nA = new LmvVector3();
    nB = new LmvVector3();
    nC = new LmvVector3();
  }

  const readPosition = makeReadPositionFunc(geometry);
  const readNormal = makeReadNormalFunc(geometry);
  const indices = getIndexBuffer(geometry);
  if (!readPosition) return;

  if (indices) {

    var groups = geometry.groups;

    if (!groups || groups.length === 0) {
      groups = [{ start: 0, count: indices.length, index: 0 }];
    }

    for (var oi = 0, ol = groups.length; oi < ol; ++oi) {

      var start = groups[oi].start;
      var count = groups[oi].count;
      var index = groups[oi].index;

      for (var i = start, il = start + count; i < il; i += 3) {

        a = index + indices[i];
        b = index + indices[i + 1];
        c = index + indices[i + 2];

        readPosition(a, vA);
        readPosition(b, vB);
        readPosition(c, vC);

        if (readNormal) {
          readNormal(a, nA);
          readNormal(b, nB);
          readNormal(c, nC);

          callback(vA, vB, vC, a, b, c, nA, nB, nC, i / 3);
        } else {
          callback(vA, vB, vC, a, b, c, null, null, null, i / 3);
        }
      }
    }

  } else {

    var vcount = getVertexCount(geometry);

    for (var i = 0; i < vcount; i += 3) {

      a = i;
      b = i + 1;
      c = i + 2;

      readPosition(a, vA);
      readPosition(b, vB);
      readPosition(c, vC);

      if (readNormal) {
        readNormal(a, nA);
        readNormal(b, nB);
        readNormal(c, nC);

        callback(vA, vB, vC, a, b, c, nA, nB, nC, i / 3);
      } else {
        callback(vA, vB, vC, a, b, c, null, null, null, i / 3);
      }
    }
  }
}


var vP, vQ;

export function enumMeshLines(geometry, callback) {

  var attributes = geometry.attributes;

  var a, b;

  if (!vP) {
    vP = new LmvVector3();
    vQ = new LmvVector3();
  }

  var istep = 2;
  if (geometry.lineWidth) {
    istep = 6;
  }

  const indices = getIndexBuffer(geometry);

  if (indices) {

    let positions, stride;
    positions = geometry.vb ? geometry.vb : attributes.position.array;
    stride = geometry.vb ? geometry.vbstride : 3;

    var groups = geometry.groups;

    if (!groups || groups.length === 0) {

      groups = [{ start: 0, count: indices.length, index: 0 }];

    }

    for (var oi = 0, ol = groups.length; oi < ol; ++oi) {

      var start = groups[oi].start;
      var count = groups[oi].count;
      var index = groups[oi].index;

      for (var i = start, il = start + count, lineIdx = start / istep; i < il; i += istep, lineIdx++) {

        a = index + indices[i];
        b = index + indices[i + 1];

        vP.x = positions[a * stride];vP.y = positions[a * stride + 1];vP.z = positions[a * stride + 2];
        vQ.x = positions[b * stride];vQ.y = positions[b * stride + 1];vQ.z = positions[b * stride + 2];

        callback(vP, vQ, a, b, lineIdx);
      }

    }

  } else {

    const positions = geometry.vb ? geometry.vb : attributes.position.array;
    const stride = geometry.vb ? geometry.vbstride : 3;

    for (var i = 0, il = positions.length / stride, lineIdx = 0; i < il; i += istep, lineIdx++) {

      a = i;
      b = i + 1;

      vP.x = positions[a * stride];vP.y = positions[a * stride + 1];vP.z = positions[a * stride + 2];
      vQ.x = positions[b * stride];vQ.y = positions[b * stride + 1];vQ.z = positions[b * stride + 2];

      callback(vP, vQ, a, b, lineIdx);
    }

  }
}


export function enumMeshEdges(geometry, callback) {

  var a, b;

  if (!vP) {
    vP = new LmvVector3();
    vQ = new LmvVector3();
  }

  var istep = 2;

  const indices = geometry.iblines;

  if (!indices) {
    return;
  }

  const positions = geometry.vb ? geometry.vb : attributes.position.array;
  const stride = geometry.vb ? geometry.vbstride : 3;

  var groups = geometry.groups;

  if (!groups || groups.length === 0) {

    groups = [{ start: 0, count: indices.length, index: 0 }];

  }

  for (var oi = 0, ol = groups.length; oi < ol; ++oi) {

    var start = groups[oi].start;
    var count = groups[oi].count;
    var index = groups[oi].index;

    for (var i = start, il = start + count; i < il; i += istep) {

      a = index + indices[i];
      b = index + indices[i + 1];

      vP.x = positions[a * stride];vP.y = positions[a * stride + 1];vP.z = positions[a * stride + 2];
      vQ.x = positions[b * stride];vQ.y = positions[b * stride + 1];vQ.z = positions[b * stride + 2];

      callback(vP, vQ, a, b);
    }

  }
}

/**
 * Iterates over all fragments of the specified model and dbId.
 *
 * This function traverses the instance tree of the model, collects all fragment IDs associated with the given dbId
 * (including its children) and callbacks fragment geometries, world matrices and IDs.
 *
 * @param {Autodesk.Viewing.Model} model - The model to collect triangles from.
 * @param {number} dbId - The database ID of the node whose triangles should be collected.
 * @param {Function} callback - Callback function that receives the geometry, world matrix, and fragment ID.
 */
function enumObjectGeometries(model, dbId, callback) {
  const instanceTree = model.getInstanceTree();
  const fragList = model.getFragmentList();
  let frags;

  if (instanceTree) {
    const selection = new Set();
    instanceTree.enumNodeChildren(dbId, (node) => {
      instanceTree.enumNodeFragments(node, (fragId) => {
        selection.add(fragId);
      }, true);
    }, true);
    frags = [];
    for (const fragId of selection) {
      frags.push(fragId);
    }
    frags.sort((a, b) => a - b);
  } else if (fragList?.fragments?.dbId2fragId?.[dbId]) {
    // performance optimization: direct mapping from dbId to fragId(s)
    const maybeArray = fragList.fragments.dbId2fragId[dbId];
    frags = Array.isArray(maybeArray) ? maybeArray : typeof maybeArray === 'number' ? [maybeArray] : [];
  } else {
    // No instance tree -- treat dbIds as fragIds
    frags = [dbId];
  }

  for (let i = 0; i < frags.length; i++) {
    const fragId = frags[i];
    const geometry = fragList.getGeometry(fragId);
    if (!geometry) continue;
    fragList.getWorldMatrix(fragId, TMP_MAT4);

    callback(geometry, TMP_MAT4, fragId);
  }
}
const TMP_MAT4 = new LmvMatrix4();

/** Get a map of edges to their indices.
 * @param {LeanBufferGeometry} geometry
 * @returns {Map<string, number[]> | null}
 */
export function getEdgeMap(geometry) {
  function getEdgeKey(vA, vB) {
    return vA < vB ? `${vA}_${vB}` : `${vB}_${vA}`;
  }

  function addEdgeToMap(vA, vB, i) {
    const key = getEdgeKey(vA, vB);
    if (!edgeMap.has(key)) {
      edgeMap.set(key, []);
    }
    edgeMap.get(key).push(i);
  }

  const ib = getIndexBuffer(geometry);
  if (!ib) return null;

  const edgeMap = new Map();
  for (let i = 0; i < ib.length; i += 3) {
    const v0 = ib[i];
    const v1 = ib[i + 1];
    const v2 = ib[i + 2];

    addEdgeToMap(v0, v1, i);
    addEdgeToMap(v1, v2, i);
    addEdgeToMap(v2, v0, i);
  }

  return edgeMap;
}

export let VertexEnumerator = {
  getVertexCount,
  makeReadPositionFunc,
  enumMeshVertices,
  enumMeshIndices,
  enumMeshTriangles,
  enumMeshLines,
  enumMeshEdges,
  enumObjectGeometries,
  getEdgeMap
};